import { PrimaryButton } from "@components/Buttons/PrimaryButton";
import { ComponentIgnoreOnPdf } from "@components/ComponentIgnoreOnPdf";
import { invoiceHexToRgbTuple, resolveInvoiceCustomStyleFromInvoice } from "@components/invoice-customization/utils/normalizeInvoiceCustomStyle";
import { Box, CircularProgress, Container, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { gateway } from "src/gateway";
import { selectActiveCompanyId } from "src/redux/features/user/selectors/userState.selectors";
import { useAppSelector } from "src/redux/hooks/redux";
import { useGlobalSnackbar } from "../../../../../context/SnackbarContext";
import { decodeToken, getRecordEmail } from "../../../../../utils";
import { useGetScopeOfWork } from "../../../../scope-of-work/hooks/useGetScopeOfWork";
import { EQueryKeys } from "../../../../subscriptions/hooks/useMySubscriptionInfo.query";
import { EInvoiceType } from "../../../create/interfaces/invoice.interfaces";
import { PaymentType } from "../../../create/PaymentMethods";
import { InvoiceCalculations } from "../../../create/shared/types";
import { useMergeInvoiceWithScopePdf } from "../../hooks/useMergeInvoiceWithScopePdf";
import { prepareHtml2CanvasClone, sanitizeCssText, trimCanvasTrailingBlank } from "../../utils/sanitizeHtml2CanvasClone";
import { syncInvoicePayment } from "./actions/syncInvoicePayment";
import { CancelledInvoiceView } from "./CancelledInvoiceView";
import { ClientInvoiceHeader } from "./ClientInvoiceHeader";
import { useGetInvoice } from "./hooks/useGetInvoice";
import { useInvoicePaymentStatus } from "./hooks/useInvoicePaymentStatus";
import { PaymentProcessingView } from "./PaymentProcessingView";

export interface ClientViewProps {
  invoiceUid?: string;
}

export interface IDecodeTokenInvoice {
  invoiceUid: string;
  jobEventsUids?: string[];

  companies: Array<{
    id: number;
  }>;

  uid: string;
  roles: Array<{
    type: string;
  }>;

  canPay: boolean;
  canView: boolean;

  iat: number;
}

export const ClientView = ({ invoiceUid: invoiceUidProp }: ClientViewProps) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { invoiceUid: invoiceUidParam } = useParams<{ invoiceUid?: string }>();
  const [isPayment, setIsPayment] = useState(false);
  const [isPdfMode, setIsPdfMode] = useState(false);
  const [awaitingPaymentConfirmation, setAwaitingPaymentConfirmation] = useState(false);
  const [processingTimedOut, setProcessingTimedOut] = useState(false);

  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const { showSnackbar } = useGlobalSnackbar();
  const { mutateAsync: mergeInvoiceWithScopePdf } = useMergeInvoiceWithScopePdf();
  const targetRef = useRef<HTMLDivElement | null>(null);

  const PROCESSING_TIMEOUT_MS = 60_000;
  const queryToken = searchParams.get("token");
  const tokenFromQuery = queryToken || sessionStorage.getItem("token");
  // Short-link tokens are opaque UUIDs; only JWTs carry decodable claims.
  const decode = (decodeToken(tokenFromQuery) ?? null) as IDecodeTokenInvoice | null;

  // Prefer explicit prop, then route param, then legacy JWT-embedded claim.
  const invoiceUid = invoiceUidProp ?? invoiceUidParam ?? decode?.invoiceUid;
  const finalInvoiceUid = invoiceUid;
  const paymentParam = searchParams.get("payment");
  const closeParam = searchParams.get("close");
  const isPopupReturn = closeParam === "true" && typeof window !== "undefined" && !!window.opener;
  const paymentSuccessReturn = paymentParam === "success" && !isPopupReturn;
  const activeCompanyId = useAppSelector(selectActiveCompanyId);

  const handleChangeStatus = (isPay: boolean) => {
    setIsPayment(isPay);
  };

  const uids = {
    invoiceUid: invoiceUid ?? "",
    jobEventsUids: decode?.jobEventsUids,
  };

  const { data: invoice, isLoading: isLoadingInvoice, isError: isErrorInvoice, refetch } = useGetInvoice(invoiceUid ?? uids, tokenFromQuery);

  const [companyClient, setCompanyClient] = useState<any>(null);
  const [isLoadingCompany, setIsLoadingCompany] = useState(false);

  useEffect(() => {
    let active = true;
    const companyId = invoice?.pricingDetail?.companyId ?? (invoice as any)?.pricingDetails?.companyId ?? activeCompanyId;
    if (invoice?.type === EInvoiceType.SERVICE_USAGE && companyId) {
      const fetchCompany = async () => {
        try {
          setIsLoadingCompany(true);
          const authToken = tokenFromQuery ?? sessionStorage.getItem("token");
          const res = await gateway.get(`/companies/${companyId}`, {
            headers: authToken ? { Authorization: `Bearer ${authToken}` } : {},
          });
          if (active) {
            setCompanyClient(res.data?.data || res.data);
          }
        } catch (error) {
          console.error("Error fetching company details:", error);
        } finally {
          if (active) {
            setIsLoadingCompany(false);
          }
        }
      };
      fetchCompany();
    } else {
      setCompanyClient(null);
    }
    return () => {
      active = false;
    };
  }, [invoice, tokenFromQuery]);

  useInvoicePaymentStatus({
    invoiceUid: finalInvoiceUid,
    onEvent: (p) => {
      refetch();
    },
    onUpdated: (payload) => {
      // Admin changed the invoice (e.g. total updated and gateway orders rotated). Force a
      // refetch so the PayPal/Stripe buttons pick the new orderId/paymentUrl, and bounce the
      // user out of the payment view so they consciously re-confirm the new amount.
      refetch();
      setIsPayment(false);
      setAwaitingPaymentConfirmation(false);
      const fmt = typeof payload.total === "number" && payload.currency ? `New total: ${payload.currency} ${payload.total.toFixed(2)}.` : "";
      showSnackbar(`This invoice was updated. ${fmt} Please review before paying.`.trim(), "info");
    },
  });

  const paidStatus = invoice?.paidWithPaymentMethod != null;

  const invoiceCompany = useMemo(() => {
    return invoice?.billingJobEvents?.[0]?.job?.company ?? invoice?.jobEvent?.job?.company ?? invoice?.quote?.company ?? companyClient ?? null;
  }, [invoice, companyClient]);

  const invoiceCompanyName =
    invoice?.type === EInvoiceType.SERVICE_USAGE ? "Flownect" : invoiceCompany?.businessName?.trim() || invoiceCompany?.name?.trim() || "Flownect";
  const invoiceCompanyGmail =
    invoice?.type === EInvoiceType.SERVICE_USAGE ? "contact@flownect.com" : invoiceCompany?.email || invoiceCompany?.businessEmail || "";
  const invoiceCompanyAddress =
    invoice?.type === EInvoiceType.SERVICE_USAGE
      ? ""
      : [invoiceCompany?.address, invoiceCompany?.city, invoiceCompany?.state, invoiceCompany?.zipCode, invoiceCompany?.country]
          .filter(Boolean)
          .join(", ");

  const hasRecentlyCompletedPayment = useMemo(() => {
    if (!Array.isArray(invoice?.payments)) return false;
    const now = Date.now();
    return invoice.payments.some((p: any) => {
      if (p.status?.toLowerCase() !== "completed") return false;
      const paidTime = p.paidAt ? new Date(p.paidAt).getTime() : 0;
      return now - paidTime < 300000; // completed in the last 5 minutes
    });
  }, [invoice?.payments]);

  const isConfirmedPaid = paidStatus || hasRecentlyCompletedPayment;
  const isProcessingPayment = (paymentSuccessReturn || awaitingPaymentConfirmation) && !isConfirmedPaid;
  const queryClient = useQueryClient();
  const isSubscriptionInvoice = invoice?.type === EInvoiceType.SERVICE_USAGE;

  useEffect(() => {
    if (isConfirmedPaid && isSubscriptionInvoice) {
      queryClient.invalidateQueries({ queryKey: [EQueryKeys.MY_SUBSCRIPTION_INFO] });
    }
  }, [isConfirmedPaid, isSubscriptionInvoice, queryClient]);

  // Clear awaiting state and timeout flag once webhook has updated the invoice
  useEffect(() => {
    if (isConfirmedPaid && awaitingPaymentConfirmation) {
      setAwaitingPaymentConfirmation(false);
      setProcessingTimedOut(false);
    }
  }, [isConfirmedPaid, awaitingPaymentConfirmation]);

  // Timeout: if confirmation does not arrive in time, show "taking longer" state with retry
  useEffect(() => {
    if (!isProcessingPayment) {
      setProcessingTimedOut(false);
      return;
    }
    const timer = window.setTimeout(() => {
      setProcessingTimedOut(true);
    }, PROCESSING_TIMEOUT_MS);
    return () => window.clearTimeout(timer);
  }, [isProcessingPayment]);

  const handleProcessingRetry = async () => {
    const uid = invoiceUid ?? finalInvoiceUid;
    try {
      if (uid) await syncInvoicePayment(uid, tokenFromQuery);
    } finally {
      refetch();
      setAwaitingPaymentConfirmation(false);
      setProcessingTimedOut(false);
    }
  };

  // Persist token so when user returns from PayPal (return URL has no token) we can restore it
  useEffect(() => {
    if (tokenFromQuery && invoiceUid) {
      sessionStorage.setItem(`stripe_return_token_${invoiceUid}`, tokenFromQuery);
    }
  }, [tokenFromQuery, invoiceUid]);

  // Ensure token is in URL when returning from payment (same tab) so invoice can load
  useEffect(() => {
    if (!paymentSuccessReturn || !invoiceUid || tokenFromQuery) return;
    const storedToken = sessionStorage.getItem(`stripe_return_token_${invoiceUid}`);
    if (storedToken) {
      const nextSearch = new URLSearchParams(searchParams);
      nextSearch.set("token", storedToken);
      navigate(`/invoices/${invoiceUid}?${nextSearch.toString()}`, { replace: true });
    }
  }, [paymentSuccessReturn, invoiceUid, tokenFromQuery, navigate, searchParams]);

  // Popup return: close and postMessage to opener
  useEffect(() => {
    if (!isPopupReturn || (paymentParam !== "success" && paymentParam !== "cancel")) return;
    const storedToken = invoiceUid ? sessionStorage.getItem(`stripe_return_token_${invoiceUid}`) : null;
    const returnToken = tokenFromQuery || storedToken;
    const returnUrl = returnToken
      ? `${window.location.origin}/invoices/${invoiceUid}?token=${returnToken}`
      : `${window.location.origin}/invoices/${invoiceUid}`;
    window.opener?.postMessage({ type: "stripe_payment_status", status: paymentParam, returnUrl }, window.location.origin);
    if (invoiceUid) sessionStorage.removeItem(`stripe_return_token_${invoiceUid}`);
    window.close();
  }, [isPopupReturn, paymentParam, invoiceUid, tokenFromQuery]);

  // Same-tab return from cancel: redirect to invoice URL with token (no processing view)
  useEffect(() => {
    if (paymentParam !== "cancel" || isPopupReturn) return;
    const storedToken = invoiceUid ? sessionStorage.getItem(`stripe_return_token_${invoiceUid}`) : null;
    const returnToken = tokenFromQuery || storedToken;
    if (invoiceUid && (returnToken || true)) {
      if (invoiceUid) sessionStorage.removeItem(`stripe_return_token_${invoiceUid}`);
      navigate(returnToken ? `/invoices/${invoiceUid}?token=${returnToken}` : `/invoices/${invoiceUid}`, { replace: true });
    }
  }, [paymentParam, isPopupReturn, invoiceUid, tokenFromQuery, navigate]);

  // When payment is confirmed (webhook + socket), clean URL so refresh does not show processing again
  useEffect(() => {
    if (!paymentSuccessReturn || !isConfirmedPaid) return;
    const next = new URLSearchParams(searchParams);
    next.delete("payment");
    next.delete("close");
    const nextObj = Object.fromEntries(next.entries());
    setSearchParams(nextObj, { replace: true });
  }, [paymentSuccessReturn, isConfirmedPaid, searchParams, setSearchParams]);

  // Listen for payment status messages from Stripe checkout window
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // Verify message origin for security
      if (event.origin !== window.location.origin) return;

      if (event.data?.type === "stripe_payment_status") {
        const { status, returnUrl } = event.data;
        if (status === "success") {
          handleChangeStatus(true);
          // If returnUrl is provided, redirect to it
          if (returnUrl) {
            window.location.href = returnUrl;
          }
          // Optionally refresh invoice data to show updated payment status
        } else if (status === "cancel") {
          handleChangeStatus(false);
          // If returnUrl is provided, redirect to it
          if (returnUrl) {
            window.location.href = returnUrl;
          }
        }
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, []);

  const { data: scopeOfWork, isLoading: isLoadingScopeOfWork } = useGetScopeOfWork({
    scopeOfWorkUid: invoice?.scopeOfWorkUid,
  });

  const invoiceCustomStyle = useMemo(() => resolveInvoiceCustomStyleFromInvoice(invoice), [invoice]);

  const isLoading = isLoadingInvoice || isLoadingScopeOfWork || isLoadingCompany;
  const isError = isErrorInvoice;

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "100vh" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (isError || !invoice) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "100vh" }}>
        <Typography variant="h6" color="error">
          Error loading invoice or invoice not found
        </Typography>
      </Box>
    );
  }

  const resolvedPaymentMethods = invoice.paymentMethods;
  const firstBilledEvent = invoice.billingJobEvents?.[0] ?? invoice.jobEvent;

  const scopeOfWorkDetailUrls = Array.from(
    new Set(
      [
        ...(invoice.billingJobEvents ?? []).map((event: any) => event?.job?.scopeOfWorkUrl).filter(Boolean),

        invoice.jobEvent?.job?.scopeOfWorkUrl,

        typeof (scopeOfWork?.meta as any)?.detailUrl === "string" ? (scopeOfWork?.meta as any)?.detailUrl : null,
      ].filter((url): url is string => typeof url === "string" && url.length > 0),
    ),
  );

  if (invoice.cancelledAt) {
    const company = firstBilledEvent?.job?.company;
    const sellerBusinessName = company?.businessName ?? undefined;
    const sellerContactEmail = company?.email ?? undefined;
    return (
      <CancelledInvoiceView sellerBusinessName={sellerBusinessName} sellerContactEmail={sellerContactEmail} sellerContactName={sellerBusinessName} />
    );
  }

  if (isProcessingPayment) {
    return <PaymentProcessingView timedOut={processingTimedOut} error={isErrorInvoice === true} onRetry={handleProcessingRetry} />;
  }

  type InvoiceClient = {
    uid: string;
    firstName: string;
    lastName: string;
    email: string;
    phone?: string;
    clientCompanyName?: string;
    address?: string | null;
    city?: string | null;
    state?: string | null;
    country?: string | null;
    zipCode?: string | null;
  };

  const client = (firstBilledEvent?.job?.client ?? invoice.quote?.client) as InvoiceClient | undefined;

  console.log("FULL INVOICE:", invoice);
  console.log("RESOLVED CLIENT:", client);
  console.log("JOB CLIENT:", firstBilledEvent?.job?.client);
  console.log("QUOTE CLIENT:", invoice.quote?.client);
  const billingCompanyName =
    invoice?.type === EInvoiceType.SERVICE_USAGE ? companyClient?.businessName : client?.clientCompanyName?.trim() || undefined;

  const billingContactName = client ? [client.firstName, client.lastName].filter(Boolean).join(" ") : undefined;

  const billingEmail = invoice?.type === EInvoiceType.SERVICE_USAGE ? companyClient?.email : getRecordEmail(client) || client?.email;

  const billingAddress =
    invoice?.type === EInvoiceType.SERVICE_USAGE
      ? companyClient?.address
      : [client?.address, client?.city, client?.state, client?.zipCode, client?.country].filter(Boolean).join(", ") || undefined;
  const pricingDetailAny = (invoice as any)?.pricingDetail ?? null;
  const persistedServiceFeeLabel =
    typeof pricingDetailAny?.serviceFeeLabel === "string" && pricingDetailAny.serviceFeeLabel.trim().length > 0
      ? pricingDetailAny.serviceFeeLabel.trim()
      : "Service Fee";
  const persistedExtraLineItems: Array<{
    id?: string | null;
    title: string;
    amount: number;
    unitPrice?: number | null;
    allocations?: Array<{ eventUid: string; amount: number; unitPrice?: number | null }>;
    appliesTo?: any;
    description?: string;
  }> = Array.isArray(pricingDetailAny?.extraLineItems)
    ? pricingDetailAny.extraLineItems
        .map((item: any) => ({
          id: item?.id ?? null,
          title: String(item?.title ?? "").trim(),
          amount: Number(item?.amount ?? 0),
          unitPrice: item?.unitPrice != null ? Number(item.unitPrice) : undefined,
          allocations: Array.isArray(item?.allocations) ? item.allocations : undefined,
          appliesTo: item?.appliesTo,
          description: String(item?.description ?? "").trim() || undefined,
        }))
        .filter((item: { title: string; amount: number }) => item.title.length > 0 && Number.isFinite(item.amount) && item.amount > 0)
    : [];
  const extraLineItemsTotal = persistedExtraLineItems.reduce((acc, item) => acc + item.amount, 0);

  const persistedDiscounts = Array.isArray(pricingDetailAny?.discounts)
    ? pricingDetailAny.discounts
        .map((item: any) => ({
          description: String(item?.description ?? "").trim(),
          amount: Number(item?.amount ?? 0),
          appliesTo: item?.appliesTo,
        }))
        .filter((item: { description: string; amount: number }) => item.description.length > 0 && Number.isFinite(item.amount) && item.amount > 0)
    : [];
  const discountsTotal = persistedDiscounts.reduce((acc: number, item: { amount: number }) => acc + item.amount, 0);
  const persistedServiceFees = Array.isArray(pricingDetailAny?.serviceFees) ? pricingDetailAny.serviceFees : [];
  const serviceFeeAmountFromFees = persistedServiceFees.reduce((acc: number, fee: any) => acc + Number(fee?.total?.value ?? 0), 0);

  // Calculate totals from invoice records
  const calculations: InvoiceCalculations = {
    subtotal: invoice.mainAreasCost?.total?.value || 0,
    serviceFeeAmount: serviceFeeAmountFromFees || invoice.increasePercentageByFrequency?.total?.value || 0,
    extraLineItemsTotal,
    extraLineItems: persistedExtraLineItems,
    discountsTotal,
    discounts: persistedDiscounts.map((item: { description: string; amount: number; appliesTo?: any }) => ({
      description: item.description,
      kind: "amount" as const,
      value: item.amount,
      amount: item.amount,
      appliesTo: item.appliesTo,
    })),
    serviceFees: persistedServiceFees,
    tipAmount: Number(invoice.tipAmount ?? invoice.pricingDetail?.tip?.amount ?? 0) || 0,
    total: invoice.totalCost?.total?.value || 0,
  };

  // Get payment method info from invoice
  const bankTransferPayment = resolvedPaymentMethods?.find((pm) => pm.provider === "bank_transfer") as any;

  const selectedPayments = resolvedPaymentMethods?.map((pm) => pm.provider) || [];
  const paymentType: PaymentType = bankTransferPayment ? "Bank Transfer" : "Other";
  const referenceNumber = bankTransferPayment?.bankTransferInfo?.routingNumber || "";

  const hasClientPaymentMethods = selectedPayments.some((provider) => provider === "paypal" || provider === "stripe" || provider === "bank_transfer");

  const hasRecipientsList = Array.isArray(invoice?.emailRecipients) && invoice.emailRecipients.length > 0;

  const hasCanPayRecipient =
    hasRecipientsList &&
    invoice.emailRecipients.some((r: any) => {
      const level = String(r?.permissionLevel || "")
        .toLowerCase()
        .trim();
      return level === "can_pay" || level === "can pay";
    });

  // emailRecipients is the definitive source of truth when present; fallback to backend/JWT flags when missing.
  const viewerCanPay = hasRecipientsList ? hasCanPayRecipient : invoice.viewerCanPay === true || decode?.canPay === true;

  const canViewPayment = hasClientPaymentMethods && viewerCanPay;

  // Format invoice number from UID
  const issuedDate = invoice.createdAt
    ? new Date(invoice.createdAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
    : new Date().toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });

  const waitForImagesToLoad = async (element: HTMLElement, timeoutMs = 5000) => {
    const images = Array.from(element.querySelectorAll<HTMLImageElement>("img"));

    await Promise.all(
      images.map((img) => {
        if (img.complete) {
          return Promise.resolve();
        }

        return new Promise<void>((resolve) => {
          const timeout = window.setTimeout(() => {
            cleanup();
            resolve();
          }, timeoutMs);

          const cleanup = () => {
            window.clearTimeout(timeout);
            img.removeEventListener("load", handleComplete);
            img.removeEventListener("error", handleComplete);
          };

          const handleComplete = () => {
            cleanup();
            resolve();
          };

          img.addEventListener("load", handleComplete, { once: true });
          img.addEventListener("error", handleComplete, { once: true });
        });
      }),
    );
  };
  const waitForNextPaint = () =>
    new Promise<void>((resolve) => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => resolve());
      });
    });

  const waitForFontsToLoad = async (families: string[]) => {
    if (typeof document === "undefined" || !("fonts" in document)) return;

    const requests = families.flatMap((family) =>
      [400, 500, 700, 800, 900].map((weight) => document.fonts.load(`${weight} 16px "${family}"`).catch(() => undefined)),
    );

    await Promise.all(requests);
    await document.fonts.ready;
  };

  const blobToBase64 = (blob: Blob): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onloadend = () => {
        const result = String(reader.result ?? "");
        const base64 = result.includes(",") ? result.split(",")[1] : result;

        resolve(base64);
      };

      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  };

  const imageUrlToDataUrl = async (imageUrl: string): Promise<string | null> => {
    try {
      const response = await fetch(imageUrl, {
        mode: "cors",
        credentials: "omit",
        cache: "no-store",
      });

      if (!response.ok) {
        throw new Error(`Image request failed: ${response.status}`);
      }

      const blob = await response.blob();

      return await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();

        reader.onloadend = () => {
          resolve(String(reader.result));
        };

        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    } catch (error) {
      console.error("[PDF] Could not convert image to Base64", {
        imageUrl,
        error,
      });

      return null;
    }
  };

  const inlineImagesForPdf = async (element: HTMLElement) => {
    const images = Array.from(element.querySelectorAll<HTMLImageElement>("img"));

    const originalSources = new Map<HTMLImageElement, string>();

    await Promise.all(
      images.map(async (img) => {
        const source = img.currentSrc || img.src;

        if (!source || source.startsWith("data:")) {
          return;
        }

        const dataUrl = await imageUrlToDataUrl(source);

        if (!dataUrl) {
          return;
        }

        originalSources.set(img, img.src);

        await new Promise<void>((resolve) => {
          const handleLoad = () => {
            cleanup();
            resolve();
          };

          const handleError = () => {
            cleanup();
            resolve();
          };

          const cleanup = () => {
            img.removeEventListener("load", handleLoad);
            img.removeEventListener("error", handleError);
          };

          img.addEventListener("load", handleLoad, { once: true });
          img.addEventListener("error", handleError, { once: true });

          img.src = dataUrl;

          if (img.complete && img.naturalWidth > 0) {
            cleanup();
            resolve();
          }
        });

        await img.decode().catch(() => undefined);
      }),
    );

    await waitForNextPaint();

    return () => {
      originalSources.forEach((originalSource, img) => {
        img.src = originalSource;
      });
    };
  };

  const handleDownloadPDF = async () => {
    if (!targetRef.current) {
      showSnackbar("Invoice content is not ready yet.", "warning");
      return;
    }

    setIsGeneratingPdf(true);
    setIsPdfMode(true);
    let restoreImages: (() => void) | undefined;
    try {
      await waitForNextPaint();
      await waitForImagesToLoad(targetRef.current);
      restoreImages = await inlineImagesForPdf(targetRef.current);
      await waitForFontsToLoad([invoiceCustomStyle.typography.fontFamily, invoiceCustomStyle.typography.headingFontFamily]);
      await waitForNextPaint();

      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "pt",
        format: "a4",
        compress: true,
      });

      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();

      const pagePaddingX = 0;
      const pagePaddingY = 0;

      const sectionGap = 8;
      const pageTopSpacing = 16;

      const availableWidth = pageWidth - pagePaddingX * 2;
      const availableHeight = pageHeight - pagePaddingY * 2;

      const [pageBgRed, pageBgGreen, pageBgBlue] = invoiceHexToRgbTuple(invoiceCustomStyle.colors.pageBackground);

      const fillPageBackground = () => {
        pdf.setFillColor(pageBgRed, pageBgGreen, pageBgBlue);
        pdf.rect(0, 0, pageWidth, pageHeight, "F");
      };

      fillPageBackground();

      const sections = Array.from(targetRef.current.querySelectorAll<HTMLElement>("[data-pdf-section]"));

      if (sections.length === 0) {
        console.warn("[PDF] No data-pdf-section elements found. Falling back to target children.");
      }

      const printableSections = sections.length > 0 ? sections : (Array.from(targetRef.current.children) as HTMLElement[]);
      let cursorY = pagePaddingY;

      const addCanvasToPdf = (canvas: HTMLCanvasElement) => {
        const imgData = canvas.toDataURL("image/png");
        const imgWidth = availableWidth;
        const imgHeight = (canvas.height * imgWidth) / canvas.width;

        if (cursorY + imgHeight > pageHeight - pagePaddingY && cursorY > pagePaddingY) {
          pdf.addPage();
          fillPageBackground();
          cursorY = pagePaddingY + pageTopSpacing;
        }

        pdf.addImage(imgData, "PNG", pagePaddingX, cursorY, imgWidth, imgHeight);
        cursorY += imgHeight + sectionGap;
      };

      const addLargeCanvasToPdf = (canvas: HTMLCanvasElement, section?: HTMLElement) => {
        const imgWidth = availableWidth;
        const imgHeight = (canvas.height * imgWidth) / canvas.width;

        if (imgHeight <= availableHeight) {
          addCanvasToPdf(canvas);
          return;
        }

        const fullPageSourceHeight = Math.floor((availableHeight * canvas.width) / availableWidth);
        const liveHeight = Math.max(1, section?.offsetHeight ?? canvas.height);
        const canvasScaleY = canvas.height / liveHeight;

        const keepTogetherRanges = section
          ? Array.from(section.querySelectorAll<HTMLElement>("[data-pdf-keep-together]"))
              .map((element) => {
                const sectionRect = section.getBoundingClientRect();
                const elementRect = element.getBoundingClientRect();

                return {
                  top: Math.max(0, Math.round((elementRect.top - sectionRect.top) * canvasScaleY)),
                  bottom: Math.min(canvas.height, Math.round((elementRect.bottom - sectionRect.top) * canvasScaleY)),
                };
              })
              .filter((range) => range.bottom > range.top)
              .sort((a, b) => a.top - b.top)
          : [];

        const getSafeSliceHeight = (sourceY: number, maxSliceHeight: number) => {
          const proposedEnd = sourceY + maxSliceHeight;

          for (const range of keepTogetherRanges) {
            const rangeHeight = range.bottom - range.top;

            if (rangeHeight > fullPageSourceHeight) continue;
            if (range.top >= proposedEnd || range.bottom <= proposedEnd) continue;

            // The proposed page break lands inside a card that can fit on a
            // fresh page. End this page before the card instead of cutting it.
            const beforeRange = range.top - sourceY;
            if (beforeRange > 0) {
              // Keep the card together unless that would leave the first page
              // almost empty (header only). Additional Charges at the bottom of
              // a full page has a large beforeRange, so it moves intact.
              const wouldLeaveMostlyEmptyPage = beforeRange < fullPageSourceHeight * 0.15;

              if (sourceY === 0 && wouldLeaveMostlyEmptyPage) return maxSliceHeight;

              return beforeRange;
            }

            // The card starts at the current source position but does not fit
            // in the remaining PDF space. Signal the caller to start a new page.
            return 0;
          }

          return maxSliceHeight;
        };

        let sourceY = 0;

        while (sourceY < canvas.height) {
          const remainingPdfHeight = pageHeight - pagePaddingY - cursorY;

          if (remainingPdfHeight <= 1) {
            pdf.addPage();
            fillPageBackground();
            cursorY = pagePaddingY + pageTopSpacing;
            continue;
          }

          const maxSliceHeight = Math.min(Math.floor((remainingPdfHeight * canvas.width) / availableWidth), canvas.height - sourceY);

          let sliceHeight = getSafeSliceHeight(sourceY, maxSliceHeight);

          if (sliceHeight <= 0) {
            const remainingCanvas = canvas.height - sourceY;
            const leftoverIsSmall = remainingPdfHeight < availableHeight * 0.28;
            const remainingIsShortTail = remainingCanvas <= fullPageSourceHeight * 0.45;

            if (cursorY > pagePaddingY && (leftoverIsSmall || remainingIsShortTail)) {
              pdf.addPage();
              fillPageBackground();
              cursorY = pagePaddingY + pageTopSpacing;
              continue;
            }

            // Defensive fallback: if rounding ever produces 0 on an empty
            // page, make forward progress rather than looping forever.
            sliceHeight = Math.min(maxSliceHeight || fullPageSourceHeight, canvas.height - sourceY);
          }

          const pageCanvas = document.createElement("canvas");
          pageCanvas.width = canvas.width;
          pageCanvas.height = sliceHeight;

          const context = pageCanvas.getContext("2d");

          if (!context) {
            throw new Error("Could not create canvas context.");
          }

          context.drawImage(canvas, 0, sourceY, canvas.width, sliceHeight, 0, 0, canvas.width, sliceHeight);

          const imgData = pageCanvas.toDataURL("image/png");
          const slicePdfHeight = (sliceHeight * imgWidth) / canvas.width;

          pdf.addImage(imgData, "PNG", pagePaddingX, cursorY, imgWidth, slicePdfHeight);

          cursorY += slicePdfHeight;
          sourceY += sliceHeight;

          if (sourceY < canvas.height && pageHeight - pagePaddingY - cursorY <= 1) {
            pdf.addPage();
            fillPageBackground();
            cursorY = pagePaddingY + pageTopSpacing;
          }
        }
      };

      // Pre-extract and sanitize stylesheets once for all sections
      const rawLiveCss = Array.from(document.styleSheets)
        .flatMap((sheet) => {
          try {
            return Array.from(sheet.cssRules).map((rule) => rule.cssText);
          } catch {
            return [];
          }
        })
        .join("\n");
      const sanitizedLiveCss = sanitizeCssText(rawLiveCss);

      const applySanitizedStyles = (clonedDocument: Document, clonedElement?: HTMLElement) => {
        if (sanitizedLiveCss) {
          const style = clonedDocument.createElement("style");
          style.textContent = sanitizedLiveCss;
          clonedDocument.head.appendChild(style);
        }
        prepareHtml2CanvasClone(clonedDocument, clonedElement);
      };

      for (let i = 0; i < printableSections.length; i++) {
        const section = printableSections[i];

        // Keep the existing full-section capture, but prevent html2canvas from
        // creating an oversized backing canvas on long invoices. Very large
        // canvases can render blank/partial depending on the browser/device.
        const MAX_CANVAS_DIMENSION = 16000;
        const sectionWidth = Math.max(1, Math.ceil(section.scrollWidth));
        const sectionHeight = Math.max(1, Math.ceil(section.scrollHeight));
        const safeScale = Math.max(0.5, Math.min(2, MAX_CANVAS_DIMENSION / sectionWidth, MAX_CANVAS_DIMENSION / sectionHeight));

        let canvas = await html2canvas(section, {
          scale: safeScale,
          useCORS: false,
          allowTaint: false,
          backgroundColor: null,
          logging: false,
          scrollX: 0,
          scrollY: 0,
          x: 0,
          y: 0,
          width: sectionWidth,
          height: sectionHeight,
          windowWidth: sectionWidth,
          windowHeight: sectionHeight,
          onclone: applySanitizedStyles,
        });

        canvas = trimCanvasTrailingBlank(canvas, [pageBgRed, pageBgGreen, pageBgBlue]);
        addLargeCanvasToPdf(canvas, section);
      }

      const invoicePdfBlob = pdf.output("blob");

      let finalPdfBlob: Blob = invoicePdfBlob;

      if (scopeOfWorkDetailUrls.length > 0) {
        const invoicePdfBase64 = await blobToBase64(invoicePdfBlob);

        const mergedResult = await mergeInvoiceWithScopePdf({
          invoicePdfBase64,
          scopeOfWorkUrls: scopeOfWorkDetailUrls,
          token: tokenFromQuery,
        });

        if (mergedResult instanceof Blob) {
          finalPdfBlob = mergedResult;
        } else {
          console.error("[handleDownloadPDF] Invalid merged PDF result:", mergedResult);
          showSnackbar("The invoice PDF was generated, but it could not be merged with the scope PDF.", "warning");
        }
      }

      const url = window.URL.createObjectURL(finalPdfBlob);
      const link = document.createElement("a");

      link.href = url;
      link.download = `invoice-${invoice.identifierId || invoice.uid}.pdf`;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("[handleDownloadPDF] Failed to download merged PDF:", error);
      showSnackbar("The PDF could not be generated. Please try again.", "error");
    } finally {
      restoreImages?.();
      setIsPdfMode(false);
      setIsGeneratingPdf(false);
    }
  };

  return (
    <Box sx={{ position: "relative", minHeight: "100%", py: 2, px: 2, bgcolor: "var(--fx-surface-base, #FFFFFF)" }}>
      <ComponentIgnoreOnPdf>
        <PrimaryButton
          onClick={handleDownloadPDF}
          disabled={isGeneratingPdf}
          sx={{
            position: "absolute",
            top: 16,
            right: 24,
            zIndex: 10,
          }}
        >
          {isGeneratingPdf ? "Generating PDF..." : invoice?.type === EInvoiceType.SERVICE_USAGE ? "Download Invoice" : "Download Invoice + Scope PDF"}
        </PrimaryButton>
      </ComponentIgnoreOnPdf>

      <Container
        maxWidth={false}
        disableGutters
        sx={{
          display: "flex",
          justifyContent: "center",
        }}
      >
        <Box
          ref={targetRef}
          sx={{
            width: 794,
            maxWidth: 794,
            boxSizing: "border-box",
            "& *": {
              boxSizing: "border-box",
            },
          }}
        >
          <ClientInvoiceHeader
            isPdfMode={isPdfMode}
            invoiceCustomStyle={invoiceCustomStyle}
            paymentStatus={paidStatus}
            billingCompanyName={billingCompanyName}
            billingContactName={billingContactName}
            billingEmail={billingEmail}
            billingAddress={billingAddress}
            calculations={calculations}
            serviceFeeLabel={persistedServiceFeeLabel}
            paymentMethods={resolvedPaymentMethods}
            handleChangeStatus={handleChangeStatus}
            onPayPalCaptureSuccess={() => {
              setAwaitingPaymentConfirmation(true);
              if (isSubscriptionInvoice) {
                queryClient.invalidateQueries({ queryKey: [EQueryKeys.MY_SUBSCRIPTION_INFO] });
              }
            }}
            paymentType={paymentType}
            referenceNumber={referenceNumber}
            canViewPayment={canViewPayment}
            total={invoice.totalCost?.total?.value || 0}
            selectedPayments={selectedPayments}
            invoiceUid={invoice.uid}
            scopeOfWork={scopeOfWork || invoice.quote?.services?.[0]?.scopeOfWork}
            invoiceDetails={invoice}
            identifierId={invoice.identifierId}
            issuedDate={issuedDate}
            notes={invoice.notes}
            companyGmail={invoiceCompanyGmail}
            companyName={invoiceCompanyName}
            companyAddress={invoiceCompanyAddress}
            companyTimeZone={invoiceCompany?.timeZone}
          />
        </Box>
      </Container>
    </Box>
  );
};
